import { Pageable } from '../mixins/pagination/pagination.mixin';
import { FacetsI } from './facets';
import { WorkspaceI } from './workspace';
import { Language } from './documentSearch';
import { Moment } from 'moment';
import { AuthorI } from './author';
import { IPeriodic } from './magazine';
import { BookCardDTO, BookTypeE } from './book';
import { MarketI } from './market';
import { PublicationStatusE } from './document';
import { LawSubBranchDTO } from './lawBranch';

export interface DocumentTypeI {
    code: string;
    documentFieldTypes: any[];
    id: number;
    label: string;
    legalWeight?: number;
    orderNumber?: number;
    showed?: boolean;
    lawSubBranches?: LawSubBranchDTO[];
}

export interface AdditionalInformationI {
    baseDocument: string;
    dateDecision: string;
    dateDocument: string;
    datePublication: string;
    entryDate: string;
    jurisdiction: string;
    number: string;
    startDateOfApplicability: string;
    endDateOfApplicability: string;
    territory: string;
    nature: string;
}

export interface DomainI {
    id: number;
    code: string;
    label: string;
}

export interface ModifierI {
    applicabilityDate?: Moment | string;
    documentDate?: Moment | string;
    memorialDate?: Moment | string;
    type?: string;
    title?: string;
    identifier?: number;
    memorialPage?: string;
}

export interface ModifierTypeI {
    type?: string;
    modifiers?: ModifierI[];
}

export interface InitReadPublicationI {
    applicationDates: ApplicationDateI[];
    linkedDocumentList: any[];
    natureList: NatureI[];
}

export interface ApplicationDateI {
    label?: string;
    applicationDate: Moment | string;
    documentDate: Moment | string;
    documentId: number;
    documentVersion: number;
}

export interface NatureI {
    id: number;
    code: string;
    description: string;
    label: string;
}

export interface StoredPublicationFilterI {
    attributes?: StoredAttributesI;
}

export interface StoredAttributesI {
    'date-to'?: string[];
    'date-from'?: string[];
    language?: Language[];
    territory?: string[];
    workspace?: string[];
    findInAccessibleOnly?: any[];
    endDateOfApplicability?: any[];
    lawBranch?: string[];
    lawSubBranch?: string[];
}

export interface PublicationFilterI {
    keys?: string;
    excludedKeys?: string;
    mode?: SearchMode;
    suggestReference?: string;
    pagination?: Pageable;
    isNumber?: boolean;
    isMultiLanguage?: boolean;
    domainId?: string;
    reference?: string;
    version?: string;
    code?: string;
    guidelineNature?: string;
    bookType?: string;
    historicalSearch?: boolean;
    documentTypeIdList?: string[];
    attributes?: AttributesI;
    exactMatchAttributes?: PairI[];
    totalItems?: number;
    searchTime?: number;
}

export interface PairI {
    key: string;
    value: string;
}

export interface AttributesI {
    endDateOfApplicability?: any[];
    findInAccessibleOnly?: any[];
    book?: string[];
    type?: string[];
    nature?: string[];
    jurisdiction?: string[];
    sourceType?: string[];
    source?: string[];
    guidelineSource?: string[];
    workspace?: string[];
    territory?: string[];
    authorId?: string[];
    'date-to'?: string[];
    'date-from'?: string[];
    language?: Language[];
    lawBranch?: string[];
    lawSubBranch?: string[];
    edition?: string[];
}

export enum SearchMode {
    AT_LEAST_ONE_KEYWORD = 'AT_LEAST_ONE_KEYWORD',
    ALL_KEYWORDS = 'ALL_KEYWORDS',
    EXACTLY_THIS_EXPRESSION = 'EXACTLY_THIS_EXPRESSION'
}

export enum PublicationTypeTabsE {
    ALL = 'ALL',
    MONOGRAPHY = 'MONOGRAPHY',
    PERIODIC = 'PERIODIC',
    DOCTRINAL_FACT_SHEET = 'DOCTRINAL_FACT_SHEET'
}

export interface RecentDocumentsByWorkspaceFromDBI {
    publicationList: PublicationI[];
    workspace: WorkspaceI;
}

export interface RecentPublicationsI {
    publicationList: PublicationI[];
    hasNext: boolean;
}

export interface RecentPeriodicsI {
    periodicList: IPeriodic[];
    hasNext: boolean;
}

export interface ContinuousPaginationBookCardsDTO {
    cardsData: BookCardDTO[];
    hasNext: boolean;
}

export interface FilteredPublicationsI {
    documentTypeIds: string[];
    publicationList: PublicationI[];
    total: number;
    facets: FacetsI;
    basicWorkspaces: WorkspaceI[];
}

export interface PublicationFolderI {
    folderId?: number;
    folderName?: string;
    id?: number;
    lastConsultationDate?: string;
    // Left out by the folder list endpoint, which only sends each publication's identity.
    publication?: PublicationI;
    reference?: string;
    type?: AllPublicationFolderE;
    updatableTrackingActivated?: boolean;
    userStatus?: string;
    version?: number;
}
export interface PublicationI {
    id?: number;
    title?: string;
    reference?: string;
    isbn?: string;
    version?: number;
    typeCode?: string; // BOOK, LEGISLATION, JURISPRUDENCE, DOSSIER PARLEMENTAIRE, etc...
    bookType?: BookTypeE; // only if typeCode is BOOK then > MONOGRAPHY, PERIODIC, else undefined
    partner?: string;
    synopsis?: string;
    evidensseId?: string;
    hits?: string;
    resume?: string;
    number?: string;
    collection?: string;
    authors?: AuthorI[];
    free?: boolean;
    magazine?: string;
    bookTitle?: string;
    bookId?: number;
    documentDate?: string;
    endApplicability?: string;
    applicability?: string;
    parutionDate?: string;
    insertionDate?: string;

    flaggedWorkspaceList?: WorkspaceI[];
    lawSubBranches?: LawSubBranchDTO[];
    status?: PublicationStatusE;
    documentType?: DocumentTypeI;
    hasAdditionalInformation?: boolean;
    additionalInformations?: AdditionalInformationI;
    additionalWorkspaces?: WorkspaceI[];
    mainWorkspace?: WorkspaceI;
    modifierTypes?: ModifierTypeI[];
    modifiers?: ModifierI[];
    content?: string;
    domContent?: Element;
    indexationTime?: string;
    publicationServiceProvider?: string;
    isFuture?: boolean;
    thumbnail?: string;
    readingTime?: number;
    markets?: MarketI[];
    format?: DocumentFormat;
}

export enum DocumentFormat {
    HTML = 'HTML',
    PDF = 'PDF'
}

export interface SimplifiedPublicationFilterI {
    title: string;
    shouldFindInAccessibleOnly: boolean;
    authorsIds?: string[];
    publishersIds: string[];
    languageCodes: string[];
    sort: string;
    documentTypesIds?: string[];
}

export interface BookAndJournalsFilterData {
    title: string;
    shouldFindInAccessibleOnly: boolean;
    authorsIds?: string[];
    publishersIds: string[];
    languageCodes: string[];
    sort: string;
    publicationTypes: PublicationTypeTabsE[];
}

export interface FolderPublicationFilterI {
    title?: string;
    documentTypesIds?: string[];
}

export interface DoctrinalFactSheetFilterI {
    title: string;
}

export interface UserFileI {
    fileName: string;
    id: number;
    type: AllPublicationFolderE.USER_FILE;
    uploadDate: string;
    shared: boolean;
}

export enum AllPublicationFolderE {
    BOOK = 'BOOK',
    BOOKPART = 'BOOKPART',
    DOCUMENT = 'DOCUMENT',
    USER_FILE = 'USER_FILE'
}
